import { useNavigationState } from "@/zustand/NavigationState";

import MediaAttachmentContainer from "./MediaAttachmentContainer";
import MediaAttachmentLoading from "./MediaAttachmentLoading";
import MediaAttachmentError from "./MediaAttachmentError";
import MediaAttachmentEmpty from "./MediaAttachmentEmpty";
import MediaAttachmentThumbnail from "./MediaAttachmentThumbnail";
import MediaAttachmentAnchor from "./MediaAttachmentAnchor";
import { useMediaAttachments } from "./useMediaAttachments";

/**
 * MediaAttachmentList component that displays media attachments with infinite loading
 * Shows media attachments in a grid layout with newest at the top
 * Loads more attachments when scrolling to the top
 *
 * This component orchestrates the various subcomponents and manages the overall
 * media attachment display flow.
 */
export default function MediaAttachmentList() {
  // Extract only the state values we need from navigation state
  const activeChatId = useNavigationState((state) => state.activeChatId);

  // Use the custom hook to manage media attachments
  const {
    isLoading,
    error,
    messagesWithMedia,
    isFullMediaListLoaded,
    scrollerRef,
    attachmentsGridRef,
    anchorRef,
    handleRetry,
  } = useMediaAttachments(activeChatId);

  // Return null if no active chat
  if (!activeChatId) {
    return null;
  }

  return (
    <MediaAttachmentContainer
      scrollerRef={scrollerRef}
      attachmentsGridRef={attachmentsGridRef}
    >
      {/* Error state */}
      {error && <MediaAttachmentError onRetry={handleRetry} />}
      {/* Display media attachments in a flat grid with newest at the top */}
      {
        <div className="w-full">
          <div className="grid grid-cols-3 gap-1">
            {messagesWithMedia.map(
              (message) =>
                message.content.media && (
                  <MediaAttachmentThumbnail
                    key={message.content.media.attachmentId}
                    messageId={message.id}
                    attachmentId={message.content.media.attachmentId}
                    chatId={message.chatId}
                    timestamp={message.timestamp}
                    filename={message.content.media.filename}
                    attachment={message.content.media}
                  />
                ),
            )}
          </div>
        </div>
      }
      {/* Empty state - only show when not loading and no error */}
      {!isLoading && !error && messagesWithMedia.length === 0 && (
        <MediaAttachmentEmpty />
      )}

      {isLoading && <MediaAttachmentLoading />}

      <MediaAttachmentAnchor
        anchorRef={anchorRef}
        isFullListLoaded={isFullMediaListLoaded}
        hasAttachments={messagesWithMedia.length > 0}
      />
    </MediaAttachmentContainer>
  );
}
